// MIDAS V3 — Palette picker (MIDAS-V3-PALETTES-TEAL-SLATE)
//
// A 24x24 button in the rail brand row showing the CURRENT palette accent as a
// 10px dot. Clicking it opens a 4-row menu (Claude / MachineID / Teal / Slate),
// each row carrying that palette's own accent swatch plus a check mark on the
// active one. Replaces the original 2-way `C / M` toggle — four palettes no
// longer fit a toggle.
//
// The menu REUSES `RailRowMenuV3` (body portal + trigger-anchored popper +
// outside-click/Esc + focus restore) via its additive optional `checked` prop,
// so there is no second menu implementation to keep in sync.
//
// Persistence is handled by the slice reducer — see midasV3Slice.ts →
// setMidasV3Theme (localStorage["midas-v3-theme"], per-browser).
//
// Layout target: sits in the rail brand row, between the `v3` chip and the
// chevron toggle. On the collapsed rail this component is omitted (the rail
// switches palette globally via the layout root class).
//
// Labels are HARDCODED ENGLISH by design (no locale keys): this is `.cc`-only
// dev/QA chrome, so no customer ever sees these strings, and keeping them out
// of the locale JSONs avoids an i18n cache-buster bump for a colour change.
//
// Tooltip via Radix `Tooltip` from v2/ui/tooltip (already used elsewhere in v3,
// e.g. MessageActionsV3). The native `title` attribute is kept as a fallback
// for when the Radix provider isn't mounted.
//
// Reduced-motion: the token sheet's global rule (`transition-duration:
// 0.001ms`) already nukes the transitions below. No extra JS guard needed.
//
// Spec: MIDAS_V3_PALETTES_TEAL_SLATE_SPEC.md §6
//   (originally MIDAS_V3_PHASE1_SCAFFOLD_SPEC.md §13 + MIDAS_V3_PHASE6 §6)
//
// `.cc` ONLY (isCcDevSurface), 2026-09-01: a dev/QA palette affordance, not a
// customer feature — it was visible to the Tajmac pilots on
// `midas.machine-id.com`. Gated in-component (same idiom as ModelPickerV3 /
// ComposerMenuV3) so every present AND future call site
// inherits the gate. Renders null off-`.cc` and for MIDAS_PILOT users.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback, useRef, useState } from "react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import { MIDAS_V3_THEMES, MidasV3Theme } from "../../midasV3Palette";
import { setMidasV3Theme } from "../../midasV3Slice";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../v2/ui/tooltip";
import RailRowMenuV3 from "./RailRowMenuV3";
import { isCcDevSurface } from "./ccSurface";

// `Record<MidasV3Theme, …>` is the compile-time pin that stands in for a test
// (spec D12): adding a palette id to MIDAS_V3_THEMES without a label AND a
// swatch here is a tsc error.
const PALETTE_LABELS: Record<MidasV3Theme, string> = {
  claude: "Claude",
  machineid: "MachineID",
  teal: "Teal",
  slate: "Slate",
};

// Literal hex, NOT `var(--accent)`: the menu renders inside the CURRENT
// palette, so a token would paint all four rows the same colour. Values are
// each palette's `--midas-v3-accent` from midas-v3-tokens.scss.
const PALETTE_SWATCH: Record<MidasV3Theme, string> = {
  claude: "#C96442",
  machineid: "#3498DB",
  teal: "#0F766E",
  slate: "#5B5BD6",
};

const DOT_SIZE = 10;

const swatchStyle = (background: string): React.CSSProperties => ({
  width: DOT_SIZE,
  height: DOT_SIZE,
  borderRadius: 999,
  background,
  display: "block",
});

export interface MidasThemeSwitcherV3Props {
  /** Optional tooltip override. Defaults to `Palette: <Name>`. */
  title?: string;
}

const MidasThemeSwitcherV3: React.FC<MidasThemeSwitcherV3Props> = ({ title }) => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((s) => s.midasV3.theme);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);

  // `.cc`-only dev surface: render NOTHING off-`.cc` (and for MIDAS_PILOT
  // users) — no reserved space in the rail brand row. All hooks above run
  // unconditionally so the hook order is stable.
  if (!isCcDevSurface()) return null;

  const tooltip = title || `Palette: ${PALETTE_LABELS[theme]}`;

  const items = MIDAS_V3_THEMES.map((id) => ({
    id,
    label: PALETTE_LABELS[id],
    icon: <span aria-hidden="true" style={swatchStyle(PALETTE_SWATCH[id])} />,
    checked: id === theme,
    onSelect: () => dispatch(setMidasV3Theme(id)),
  }));

  return (
    <>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={open}
            // Keep the native `title` as a no-Tooltip fallback. Radix sets
            // aria-describedby when active, so screen readers don't read both.
            title={tooltip}
            aria-label={tooltip}
            style={{
              width: 24,
              height: 24,
              padding: 0,
              borderRadius: 6,
              background: "transparent",
              border: "1px solid var(--midas-v3-border)",
              color: "var(--midas-v3-muted)",
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
              // 250ms — longer than the 120ms hover transitions elsewhere so
              // the palette swap reads as a deliberate "settle". The reduced-
              // motion override in midas-v3-tokens.scss reduces all transitions
              // to 0.001ms when users prefer reduced motion.
              transition:
                "background-color 250ms ease, color 250ms ease, border-color 250ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--midas-v3-card-2)";
              e.currentTarget.style.color = "var(--midas-v3-ink)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--midas-v3-muted)";
            }}
          >
            <span
              aria-hidden="true"
              style={swatchStyle("var(--midas-v3-accent)")}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <span
            style={{
              fontFamily:
                "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 11,
            }}
          >
            {tooltip}
          </span>
        </TooltipContent>
      </Tooltip>
      <RailRowMenuV3
        open={open}
        onClose={close}
        triggerRef={triggerRef}
        ariaLabel="Palette"
        items={items}
      />
    </>
  );
};

export default MidasThemeSwitcherV3;
